import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { NgForOf } from "@angular/common";
import { TemplateService } from "../../../_services/template.service";
import { Router } from "@angular/router";
import { PlanTemplateSearchCriteria } from "../../../_models/PlanTemplateSearchCriteria";
import { PlanTemplate } from "../../../_models/PlanTemplate";
import { Canvas, Rect } from "fabric";

@Component({
  standalone: true,
  templateUrl: "./select-template.component.html",
  imports: [NgForOf],
  encapsulation: ViewEncapsulation.None,
  styleUrls: ["select-template.component.css"]
})
export class SelectTemplateComponent implements OnInit {

  public templates: PlanTemplate[] = [];

  constructor(
    private templateService: TemplateService,
    private router: Router
  ) {}

  ngOnInit() {
    this.getTemplates();
  }

  getTemplates() {
    const searchCriteria = new PlanTemplateSearchCriteria();
    searchCriteria.parkGroupIds = [];
    searchCriteria.campaignIds = [];
    searchCriteria.customerIds = [];
    searchCriteria.query = '';
    searchCriteria.pageSize = 999999;

    this.templateService.getPlanTemplatesByUser()
      .subscribe(planTemplates => {
        this.templates = planTemplates;
      });
  }

  createQuote(templateId: string) {
    this.router.navigate(['/quotes/create/' + templateId]);
  }

  createTemplateImage(data: string): string {
    if (!data) return '';

    const canvasEl = document.createElement('canvas');
    canvasEl.width = 200;
    canvasEl.height = 200;

    const canvas = new Canvas(canvasEl, {
      backgroundColor: '#f3f3f3',
      renderOnAddRemove: false
    });

    let rects: any[];

    try {
      rects = JSON.parse(data).map((r: any) => ({
        ...r,
        x: Number(r.x),
        y: Number(r.y),
        width: Number(r.width),
        length: Number(r.length)
      }));
    } catch {
      console.error('Invalid planData JSON', data);
      return '';
    }

    // 🔢 Correct draw order
    rects.sort(
      (a, b) => this.getLayerPriority(a.type) - this.getLayerPriority(b.type)
    );

    // 📐 Bounds
    const minX = Math.min(...rects.map(r => r.x));
    const minY = Math.min(...rects.map(r => r.y));
    const maxX = Math.max(...rects.map(r => r.x + r.width));
    const maxY = Math.max(...rects.map(r => r.y + r.length));

    const layoutWidth = maxX - minX;
    const layoutHeight = maxY - minY;

    // 🔑 SVG-style uniform scale
    const scale = Math.min(
      canvas.width! / layoutWidth,
      canvas.height! / layoutHeight
    );

    // 🔑 Anchor to top-left
    const offsetX = -minX * scale;
    const offsetY = -minY * scale;

    rects.forEach(r => {
      // 🧠 SNAP EDGES TO INTEGER PIXELS
      const left   = Math.round(r.x * scale + offsetX);
      const top    = Math.round(r.y * scale + offsetY);
      const right  = Math.round((r.x + r.width) * scale + offsetX);
      const bottom = Math.round((r.y + r.length) * scale + offsetY);
      const width  = right - left;
      const height = bottom - top;

      canvas.add(new Rect({
        left,
        top,
        width,
        height,
        fill: this.getColour(r.type) || '#cccccc',

        // 🔥 FIXES GAPS AND ARTIFACTS
        stroke: null,
        strokeWidth: 0,
        strokeUniform: true,
        noScaleCache: false,
        selectable: false,
        objectCaching: false
      }));
    });

    canvas.renderAll();

    const dataUrl = canvas.toDataURL({
      format: 'png',
      multiplier: 1
    });

    canvas.dispose();
    return dataUrl;
  }

  getColour(type: string | null | undefined): string {
    if (!type) return '#cccccc';

    const t = type.toLowerCase();

    if (t.includes('deck')) return '#6c83ff';     // blue
    if (t.includes('skirt')) return '#66e26f';    // green
    if (t.includes('step')) return '#ffb74d';     // orange
    if (t.includes('gate')) return '#000000';     // black

    return '#cccccc';
  }

  getLayerPriority(type: string | null | undefined): number {
    if (!type) return 0;

    const t = type.toLowerCase();

    if (t.includes('skirt')) return 0;
    if (t.includes('deck')) return 1;
    if (t.includes('walk') || t.includes('ramp')) return 2;
    if (t.includes('step')) return 3;
    if (t.includes('gate')) return 4;

    return 1;
  }
}
